<template>
    <div
        @focusin="onFocus"
        @focusout="onFocusout"
    >
        <show-all-new
            :title="pageTitle"
            :items="listItems"
            :is-loading="isLoading"
            @item-selected="itemSelected"
            @request-more="fetchMoreItems"
        />
    </div>
</template>
 
<script lang="ts" setup>
    import { computed, type ComputedRef } from "vue";
    import type { OnDemandCardData } from "@/components/Cards/types";
    import { PlayItem } from "@ca.vmedia.tv/domains";
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";
    import { Strings } from "@/locales/types/enums/strings";
    import { useI18n } from "vue-i18n";
    import { useRouter } from "vue-router";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import { useVodPageStore } from "@/stores/VodPage";
    import { storeToRefs } from 'pinia';
    import type { IEventSelected } from "@/components/MultiLineView/types";
    import { useApplicationStore } from "@/stores/Application";

    const VodPageStore = useVodPageStore();
    const {
        loadShowAllSubGenresNextPage,
    } = VodPageStore;
    const {
        getCurrentGenre: genre,
        getShowAllSubGenresCards: listItems,
        getIsLoadingShowAll: isLoading
    } = storeToRefs(VodPageStore);

    const { 
        setViewedDetailItems,
    } = useDetailPageStore();

    const ApplicationStore = useApplicationStore();
    const {
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;

    definePageMeta({
        layout: 'default-no-header' ,
        middleware: ['set-page-indicator', 'fetch-vod-show-all-sub-items'],
    });

    const router = useRouter();
    const { t } = useI18n();

    const { $navigator } = useNuxtApp();

    const pageTitle: ComputedRef<string> = computed((): string => genre.value?.name ?? t(Strings.VOD_CHANNELS).toString());


    const itemSelected = (event: IEventSelected): void => {
        const item = event.item as OnDemandCardData;

        if (PlayItem.isSeries(item.getPlayItem())) {
            gotoSeriesDetailsPage(item);
        } else {
            gotoMovieDetailsPage(item);
        }
    };
    const gotoMovieDetailsPage = (item: OnDemandCardData): void => {
        try {
            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(item));
            }
            setViewedDetailItems(playItem);
            router.push(`${linkItems.detailsVodMovies.routerPath}/${item.getId()}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true, showingTime: 5000});
        }
    };
    const gotoSeriesDetailsPage = (item: OnDemandCardData): void => {
        try {
            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(item));
            }
            setViewedDetailItems(playItem);
            router.push(`${linkItems.detailsVodSeries.routerPath}/${item.getId()}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true, showingTime: 5000});
        }
    };
    const fetchMoreItems = async (): Promise<void> => {
        await loadShowAllSubGenresNextPage();
    }

    const onFocus = () => {
        registerKeyListeners();
    };
    const onFocusout = () => {
        unregisterKeyListeners();
    };
    const keyBackListener = (): void => {
        router.go(-1);
    };
    const registerKeyListeners = (): void => {
        $navigator.on("keypress:back", keyBackListener);
    };
    const unregisterKeyListeners = (): void => {
        $navigator.off("keypress:back", keyBackListener);
    };
</script>
